import React from "react";
import { HighlightText } from "../../components/HighlightText";
import { interpolate, spring, useVideoConfig } from "remotion";
import { BulletItem } from "../../types";
import { CompanyBadge } from "../../components/CompanyBadge";
import { detectBrandInText } from "../../components/companyDetector";

// ─── Highlight Text ──────────────────────────────────────────────────────────

// ─── Insights ────────────────────────────────────────────────────────────────

interface VintageInsightsProps {
  bullets: BulletItem[];
  globalFrame: number;
}

export const VintageInsights: React.FC<VintageInsightsProps> = ({
  bullets,
  globalFrame,
}) => {
  const { fps } = useVideoConfig();
  const currentMs = (globalFrame / fps) * 1000;

  // Determine active bullet
  let activeIndex = bullets.findIndex(
    (b) => currentMs >= b.startMs && currentMs <= b.endMs,
  );

  if (activeIndex === -1) {
    const pastBullets = bullets.filter((b) => b.endMs <= currentMs);
    activeIndex = pastBullets.length > 0 ? pastBullets.length - 1 : 0;
  }

  const activeBullet = bullets[activeIndex];
  const bulletStartFrame = (activeBullet.startMs / 1000) * fps;
  const elapsedFrames = Math.max(0, globalFrame - bulletStartFrame);

  // Spring entrance for bullet text
  const bulletOpacity = interpolate(elapsedFrames, [0, 10], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const bulletTranslateY = interpolate(elapsedFrames, [0, 14], [18, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Card entrance (from very first bullet)
  const firstBulletStart = (bullets[0].startMs / 1000) * fps;
  const cardElapsed = Math.max(0, globalFrame - firstBulletStart);

  const cardOpacity = interpolate(cardElapsed, [0, 14], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const cardScale = spring({
    frame: cardElapsed,
    fps,
    config: { damping: 18, mass: 0.7, stiffness: 90 },
  });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        zIndex: 10,
        margin: "auto 0",
        padding: "10px 0",
      }}
    >
      <div
        style={{
          width: "100%",
          background: "rgba(26,18,8,0.9)",
          border: "1px solid rgba(212,168,83,0.4)",
          padding: "44px",
          borderRadius: "2px",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          position: "relative",
          opacity: cardOpacity,
          transform: `scale(${cardScale})`,
        }}
      >
        {/* Top decoration: three small gold circles */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {["●", "●", "●"].map((dot, i) => (
            <span
              key={i}
              style={{
                color: "#d4a853",
                fontSize: "8px",
                lineHeight: 1,
              }}
            >
              {dot}
            </span>
          ))}
        </div>

        {/* Dot pagination + brand badge */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "8px",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            {bullets.map((_, i) => (
              <div
                key={i}
                style={{
                  width: i === activeIndex ? 20 : 6,
                  height: 6,
                  borderRadius: 3,
                  background:
                    i === activeIndex ? "#d4a853" : "rgba(212,168,83,0.25)",
                }}
              />
            ))}
          </div>
          {(() => {
            const brand = detectBrandInText(activeBullet.text);
            return brand ? (
              <CompanyBadge
                name={brand}
                color="#d4a853"
                accentColor="#d4a853"
                size={68}
                style="minimal"
                opacity={bulletOpacity}
              />
            ) : null;
          })()}
        </div>

        {/* Thin gold divider */}
        <div
          style={{
            width: "100%",
            height: "1px",
            background: "rgba(212,168,83,0.3)",
          }}
        />

        {/* Bullet text with spring entrance */}
        <div
          style={{
            opacity: bulletOpacity,
            transform: `translateY(${bulletTranslateY}px)`,
            minHeight: "260px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <p
            style={{
              fontFamily: "'Libre Baskerville', serif",
              fontSize: "50px",
              fontWeight: 400,
              fontStyle: "italic",
              color: "#f5ede0",
              lineHeight: 1.5,
              textAlign: "center",
              margin: 0,
              padding: 0,
            }}
          >
            <HighlightText
              highlightStyle={{
                fontWeight: 700,
                color: "#d4a853",
                fontStyle: "italic",
                textDecoration: "underline",
                textDecorationColor: "rgba(212,168,83,0.4)",
                textUnderlineOffset: "4px",
                display: "inline",
              }}
              text={activeBullet.text}
              highlights={activeBullet.highlights}
            />
          </p>
        </div>

        {/* Bottom double line rule */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            width: "100%",
          }}
        >
          <div
            style={{
              width: "100%",
              height: "1px",
              background: "rgba(212,168,83,0.3)",
            }}
          />
          <div
            style={{
              width: "100%",
              height: "1px",
              background: "rgba(212,168,83,0.3)",
            }}
          />
        </div>
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
